// EN mirror of /guides — long-form guide index
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BRAND_URL } from "@/lib/brand";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const TITLE = "Guides — Translation, Apostille & Thai Visa Handbooks | NYC Language Institute";
const DESC = "Long-form walkthroughs for translation certification, MFA + Apostille chains. Ask us anything — real experts reply within minutes.";

const GUIDES = [
  { slug: "how-to-legalize-a-birth-certificate", title: "How to legalize a Thai birth certificate for overseas use", mins: 12, cat: "Apostille" },
  { slug: "translate-and-notarize-diploma", title: "Translate + notarize a university diploma (with sample layout)", mins: 9, cat: "Education" },
  { slug: "non-b-visa-complete-guide", title: "Non-B work visa: complete 2026 guide with document checklist", mins: 18, cat: "Visa" },
  { slug: "ltr-visa-eligibility", title: "LTR visa eligibility, income proof, and 10-year renewal reality", mins: 14, cat: "Visa" },
  { slug: "dtv-remote-worker", title: "DTV for remote workers: what actually gets approved", mins: 11, cat: "Visa" },
  { slug: "apostille-vs-embassy-legalization", title: "Apostille vs embassy legalization — which one do you need?", mins: 8, cat: "Apostille" },
  { slug: "marriage-abroad-with-thai-spouse", title: "Marriage abroad with a Thai spouse: 6 destination playbooks", mins: 16, cat: "Family" },
  { slug: "police-clearance-under-10-days", title: "Thai police clearance certificate in under 10 days", mins: 7, cat: "Documents" },
  { slug: "boi-application-english", title: "BOI application in English: category selection & pitfalls", mins: 15, cat: "Corporate" },
  { slug: "naati-vs-certified-translation", title: "NAATI vs Thai certified translation: what Australia actually accepts", mins: 10, cat: "Translation" },
];

export const Route = createFileRoute("/en/guides")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/guides", "en")}, { name: "description", content: ctaDesc(DESC, "/en/guides", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/guides", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/guides", "en")},
      { property: "og:url", content: `${BRAND_URL}/en/guides` }, { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${BRAND_URL}/en/guides` },
      ...hreflangLinks("/guides", "/en/guides"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "CollectionPage",
        name: TITLE, url: `${BRAND_URL}/en/guides`, inLanguage: "en",
      }),
    }],
  }),
  component: EnGuides,
});

function EnGuides() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Guides & <span className="text-amber-300">handbooks</span></h1>
          <p className="mt-3 max-w-2xl text-white/80">Practitioner-authored walkthroughs — each guide reflects an actual case we've handled, not scraped generic advice.</p>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {GUIDES.map((g) => (
            <article key={g.slug} className="rounded-2xl border border-slate-200 bg-white p-5">
              <BookOpen className="h-6 w-6 text-amber-600" aria-hidden />
              <span className="mt-3 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">{g.cat}</span>
              <h2 className="mt-2 text-base font-bold text-navy-deep">{g.title}</h2>
              <p className="mt-1 text-xs text-slate-500">{g.mins} min read</p>
              <Link to="/en/blog" className="mt-3 inline-block text-xs font-bold text-amber-700">Read on EN blog →</Link>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-6xl">
          <RelatedHubGrid locale="en" />
        </div>
      </section>
    </main>
  );
}
